import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { ArrowRight, Target, CheckCircle } from "@phosphor-icons/react";

/**
 * ActivationCard — the single highest-leverage onboarding nudge.
 *
 * Renders on the Dashboard when the user's nonprofit profile is below the
 * activation threshold (<60%). Shows: progress bar, missing fields with the
 * concrete reason each one matters, and one clear CTA to /profile. Not
 * dismissable — but not blocking either; the user can scroll past it.
 *
 * Returns null if the user is activated, or while still loading (avoids
 * flashing the card to fully-activated users).
 */
const ActivationCard = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/profile/activation-status`);
        if (alive) setStatus(res.data);
      } catch {
        // Silent — never block dashboard load on this
      }
    })();
    return () => { alive = false; };
  }, []);

  // Hide card entirely while loading OR if user is already activated
  if (!status || status.is_activated) return null;

  const { percent, filled, total, missing_fields = [] } = status;
  // Show top 4 missing fields max (more becomes overwhelming)
  const topMissing = missing_fields.slice(0, 4);

  return (
    <Card
      className="bg-gradient-to-br from-emerald-500/8 via-zinc-950 to-zinc-950 border-emerald-500/40 border-2"
      data-testid="profile-activation-card"
    >
      <CardContent className="p-6 sm:p-7">
        <div className="flex items-start gap-4 mb-5">
          <div className="shrink-0 w-12 h-12 rounded-sm bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <Target weight="duotone" className="w-7 h-7 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] text-emerald-300 font-bold uppercase tracking-[0.18em]">
                Unlock grant matching
              </span>
            </div>
            <h2 className="text-xl font-bold text-white leading-tight">
              Your profile is {percent}% complete.
            </h2>
            <p className="text-zinc-400 text-sm mt-1">
              Without these fields, FlashGrants <i>can&apos;t</i> tell you which grants you have a real shot at winning.
              Takes 2 minutes. Promise.
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-5" data-testid="activation-progress">
          <div className="flex justify-between text-[11px] text-zinc-500 mb-1.5">
            <span className="font-mono">{filled} of {total} fields</span>
            <span className="font-mono">{percent}%</span>
          </div>
          <div className="w-full bg-zinc-900 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        {/* Missing fields with "why this matters" microcopy */}
        {topMissing.length > 0 && (
          <ul className="space-y-2.5 mb-6" data-testid="activation-missing-list">
            {topMissing.map((f) => (
              <li key={f.key} className="flex items-start gap-3" data-testid={`missing-field-${f.key}`}>
                <div className="shrink-0 mt-0.5 w-4 h-4 rounded-full border-2 border-zinc-700" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-semibold leading-tight">{f.label}</p>
                  <p className="text-[12px] text-zinc-500 leading-snug mt-0.5">{f.why}</p>
                </div>
              </li>
            ))}
            {missing_fields.length > topMissing.length && (
              <li className="text-[11px] text-zinc-600 italic pl-7">
                +{missing_fields.length - topMissing.length} more on the profile page
              </li>
            )}
          </ul>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <Link to="/profile" className="flex-1 sm:flex-none">
            <Button
              className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-6 py-5 text-sm"
              data-testid="activation-finish-profile-btn"
            >
              Finish my profile
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          {filled > 0 && (
            <p className="text-[11px] text-zinc-500 flex items-center gap-1.5">
              <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-500" />
              You&apos;re {filled}/{total} of the way there
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivationCard;
